/**
 * useAppointments Hook
 *
 * Fetches and manages appointments for a task.
 * Returns appointments indexed by move_type for easy lookup by route nodes.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import appointmentApi from '../services/appointmentApi';
import { socketService } from '../services/socket';
import type { Appointment } from '../types/appointment';

interface UseAppointmentsOptions {
  /** Task DB ID (numeric). If undefined/null, hook is disabled. */
  taskId?: number | null;
  /** Whether to fetch on mount. Default true. */
  enabled?: boolean;
}

interface UseAppointmentsReturn {
  /** All appointments for this task, sorted by sequence */
  appointments: Appointment[];
  /** Map: moveType → Appointment (latest non-cancelled) for quick node lookup */
  byMoveType: Record<string, Appointment>;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Re-fetch appointments */
  refresh: () => Promise<void>;
}

export function useAppointments(options: UseAppointmentsOptions): UseAppointmentsReturn {
  const { taskId, enabled = true } = options;
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchAppointments = useCallback(async () => {
    if (!taskId || !enabled) {
      setAppointments([]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const data = await appointmentApi.getByTaskId(taskId);
      // Prevent stale response from overwriting newer data
      if (currentRequestId !== requestIdRef.current) return;
      setAppointments(data);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch appointments');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [taskId, enabled]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Auto-refresh when socket pushes appointment changes for this task
  useEffect(() => {
    if (!taskId || !enabled) return;

    const handleUpdate = (data: unknown) => {
      const d = data as { taskId?: number };
      if (d.taskId === taskId) {
        fetchAppointments();
      }
    };

    socketService.on('appointment_updated', handleUpdate);
    socketService.on('appointment_cancelled', handleUpdate);
    socketService.on('appointments_bulk_updated', handleUpdate);

    return () => {
      socketService.off('appointment_updated', handleUpdate);
      socketService.off('appointment_cancelled', handleUpdate);
      socketService.off('appointments_bulk_updated', handleUpdate);
    };
  }, [taskId, enabled, fetchAppointments]);

  // Build moveType → Appointment lookup (latest non-cancelled per moveType)
  const byMoveType = useMemo(() => {
    const map: Record<string, Appointment> = {};
    for (const appt of appointments) {
      if (['cancelled', 'rejected'].includes(appt.status)) continue;
      // Later entries (higher id) win — they are more recent
      map[appt.moveType] = appt;
    }
    return map;
  }, [appointments]);

  return {
    appointments,
    byMoveType,
    loading,
    error,
    refresh: fetchAppointments,
  };
}

export default useAppointments;
